/**
 * Midway authentication configuration.
 *
 * In Amazon-internal deployments, Midway authentication is normally enforced
 * at the edge (CloudFront + Lambda@Edge, or an ALB Midway integration). The
 * browser receives a Midway session cookie, and this app reads the resolved
 * identity from a small "who am I" endpoint behind that protected edge.
 *
 * For local development, set VITE_MIDWAY_DEV_MODE=true to skip the redirect
 * and use a mock user instead.
 */
export interface MidwayConfig {
  /** When true, skip Midway redirect and use a mock user (local dev only). */
  devMode: boolean;
  /** Endpoint returning the authenticated user as JSON. */
  userEndpoint: string;
  /** Midway SSO sign-in URL. */
  signInUrl: string;
  /** Midway sign-out URL. */
  signOutUrl: string;
}

function readBool(value: string | undefined, fallback = false): boolean {
  if (value === undefined) return fallback;
  return value.toLowerCase() === 'true';
}

export const midwayConfig: MidwayConfig = {
  devMode: readBool(import.meta.env.VITE_MIDWAY_DEV_MODE, import.meta.env.DEV),
  userEndpoint: import.meta.env.VITE_MIDWAY_USER_ENDPOINT ?? '/api/auth/me',
  signInUrl:
    import.meta.env.VITE_MIDWAY_SIGN_IN_URL ??
    'https://midway-auth.amazon.com/login',
  signOutUrl:
    import.meta.env.VITE_MIDWAY_SIGN_OUT_URL ??
    'https://midway-auth.amazon.com/logout',
};

/**
 * Builds the Midway sign-in URL with the current location set as the
 * post-login redirect target.
 */
export function buildSignInRedirect(): string {
  const redirectTarget = encodeURIComponent(window.location.href);
  const separator = midwayConfig.signInUrl.includes('?') ? '&' : '?';
  return `${midwayConfig.signInUrl}${separator}redirect_uri=${redirectTarget}`;
}
